/**
 * Form to submit a new holiday (leave) request with an interactive visual calendar modal.
 */
import React, { useState, useEffect, useMemo } from 'react';
import hrApi from '../api';
import {
  toDateKey,
  publicHolidayDateSet,
  countRequestedLeaveDays,
  isWorkingDayForEmployee,
  getWorkingDayFraction,
} from '../utils/workingDays';
import './DaysTakenBreakdownModal.css';

const REQUEST_TYPES = [
  { code: 'annual', name: 'Annual leave' },
  { code: 'overtime', name: 'Overtime leave' },
  { code: 'illness', name: 'Illness', instructions: 'Include medical certificate if more than 3 days.' },
  { code: 'compensation-extra-hours', name: 'Compensation for extra hours' },
  { code: 'doctor-appointment', name: 'Doctor appointment' },
  { code: 'birth-adoption', name: 'Birth or adoption' },
  { code: 'house-move', name: 'House move' },
  { code: 'unpaid-leave', name: 'Unpaid leave' },
  { code: 'care-family-member', name: 'Care of family member' },
  { code: 'accident', name: 'Accident' },
  { code: 'military-service', name: 'Military service', instructions: 'Attach copy of marching orders.' },
  { code: 'death-family-member', name: 'Death of family member' },
  { code: 'marriage', name: 'Marriage' },
  { code: 'training', name: 'Training', instructions: 'Specify name of the course.' },
  { code: 'working-remotely', name: 'Working remotely' },
  { code: 'other', name: 'Other', instructions: 'Specify reason.' },
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatDateForInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatReadableDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDayHeading(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

function getContractHalfDayForDate(dateStr, employeeWorkingSettings) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const weekday = d.getDay();
  const sched = employeeWorkingSettings?.daySchedule && typeof employeeWorkingSettings.daySchedule === 'object'
    ? employeeWorkingSettings.daySchedule[String(weekday)]
    : null;
  return sched === 'morning' || sched === 'afternoon' ? sched : null;
}

function HolidayRequestForm({ onSubmit, loading }) {
  const now = new Date();
  const todayStr = formatDateForInput(now);
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [halfDays, setHalfDays] = useState({}); // { 'YYYY-MM-DD': 'am' | 'pm' }
  const [editingDateKey, setEditingDateKey] = useState(null); // currently selected day in the calendar for Full/AM/PM choice
  const [comment, setComment] = useState('');
  const [leaveTypeCode, setLeaveTypeCode] = useState('annual');
  const [leaveTypeOpen, setLeaveTypeOpen] = useState(false);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [employeeWorkingSettings, setEmployeeWorkingSettings] = useState(null);
  const [overrideWindows, setOverrideWindows] = useState([]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth()); // 0..11
  const [selectingAnchor, setSelectingAnchor] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const ACCEPT_ATTACHMENTS = '.pdf,.doc,.docx,image/jpeg,image/png,image/gif,image/webp';
  const MAX_ATTACHMENTS = 5;
  const MAX_FILE_MB = 10;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const maxBytes = MAX_FILE_MB * 1024 * 1024;
    const valid = files.filter((f) => f.size <= maxBytes);
    if (valid.length !== files.length) return;
    setSelectedFiles((prev) => [...prev, ...valid].slice(0, MAX_ATTACHMENTS));
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    hrApi
      .get('/api/default-working-hours')
      .then((res) => {
        const wd = res.data?.workingDays;
        const workingDays = Array.isArray(wd) && wd.length > 0 ? wd : [1, 2, 3, 4, 5];
        const ds = res.data?.daySchedule;
        const daySchedule = ds && typeof ds === 'object' && !Array.isArray(ds) ? ds : undefined;
        setEmployeeWorkingSettings({ workingDays, daySchedule });
      })
      .catch(() => setEmployeeWorkingSettings(null));
    hrApi
      .get('/api/work-schedule-overrides/mine')
      .then((res) => setOverrideWindows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOverrideWindows([]));
  }, []);

  const yearsNeeded = useMemo(() => {
    const set = new Set([viewYear]);
    if (startDate) {
      const y1 = new Date(`${startDate}T12:00:00`).getFullYear();
      if (!Number.isNaN(y1)) set.add(y1);
    }
    if (endDate) {
      const y2 = new Date(`${endDate}T12:00:00`).getFullYear();
      if (!Number.isNaN(y2)) set.add(y2);
    }
    return Array.from(set).sort();
  }, [startDate, endDate, viewYear]);

  useEffect(() => {
    if (yearsNeeded.length === 0) {
      setPublicHolidays([]);
      return;
    }
    const fetches = yearsNeeded.map((year) =>
      hrApi.get('/api/public-holidays', { params: { year } }).then((res) => ({ year, list: Array.isArray(res.data) ? res.data : [] }))
    );
    Promise.all(fetches)
      .then((results) => {
        setPublicHolidays(results.flatMap((r) => r.list));
      })
      .catch(() => setPublicHolidays([]));
  }, [yearsNeeded.join(',')]);

  const holidaySet = useMemo(() => publicHolidayDateSet(publicHolidays), [publicHolidays]);

  const activeHalfDays = useMemo(() => {
    if (!startDate || !endDate) return {};
    const out = {};
    for (const [k, v] of Object.entries(halfDays)) {
      if (k >= startDate && k <= endDate && (v === 'am' || v === 'pm')) {
        out[k] = v;
      }
    }
    return out;
  }, [halfDays, startDate, endDate]);

  const { legacyHalfDay, legacyHalfDayOn } = useMemo(() => {
    const keys = Object.keys(activeHalfDays);
    if (keys.length === 0) return { legacyHalfDay: null, legacyHalfDayOn: null };
    if (startDate === endDate && activeHalfDays[startDate]) {
      return { legacyHalfDay: activeHalfDays[startDate], legacyHalfDayOn: null };
    }
    if (keys.length === 1) {
      if (keys[0] === startDate) return { legacyHalfDay: activeHalfDays[startDate], legacyHalfDayOn: 'start' };
      if (keys[0] === endDate) return { legacyHalfDay: activeHalfDays[endDate], legacyHalfDayOn: 'end' };
    }
    return { legacyHalfDay: null, legacyHalfDayOn: null };
  }, [activeHalfDays, startDate, endDate]);

  const daysTaken = useMemo(() => {
    if (!startDate || !endDate) return null;
    const start = new Date(`${startDate}T12:00:00`);
    const end = new Date(`${endDate}T12:00:00`);
    if (end < start) return null;
    return countRequestedLeaveDays(
      start,
      end,
      holidaySet,
      employeeWorkingSettings,
      overrideWindows,
      null,
      legacyHalfDay,
      legacyHalfDayOn,
      activeHalfDays
    );
  }, [startDate, endDate, holidaySet, employeeWorkingSettings, overrideWindows, legacyHalfDay, legacyHalfDayOn, activeHalfDays]);

  const getDayStatus = (dateKey) => {
    const d = new Date(`${dateKey}T12:00:00`);
    const weekday = d.getDay();
    const isWeekend = weekday === 0 || weekday === 6;
    const isHoliday = holidaySet.has(dateKey);
    if (isHoliday) return { kind: 'holiday', canToggleHalf: false, fraction: 0 };
    if (isWeekend) return { kind: 'weekend', canToggleHalf: false, fraction: 0 };
    const isWorking = isWorkingDayForEmployee(dateKey, employeeWorkingSettings, overrideWindows, null);
    if (!isWorking) return { kind: 'non_working', canToggleHalf: false, fraction: 0 };
    const fraction = getWorkingDayFraction(dateKey, employeeWorkingSettings, overrideWindows, null);
    const contractHalf = getContractHalfDayForDate(dateKey, employeeWorkingSettings);
    const isSingleDay = startDate === endDate;
    const canToggleHalf = fraction >= 1 || isSingleDay;
    return { kind: fraction < 1 ? 'contract_half' : 'working', canToggleHalf, fraction, contractHalf };
  };

  const setDatePortion = (dateKey, portion) => {
    if (!dateKey) return;
    const status = getDayStatus(dateKey);
    if (!status.canToggleHalf) return;
    setHalfDays((prev) => {
      const copy = { ...prev };
      if (portion === 'full' || !portion) delete copy[dateKey];
      else copy[dateKey] = portion;
      return copy;
    });
  };

  const handleCalendarCellClick = (dateKey) => {
    if (selectingAnchor) {
      const a = selectingAnchor <= dateKey ? selectingAnchor : dateKey;
      const b = selectingAnchor <= dateKey ? dateKey : selectingAnchor;
      setStartDate(a);
      setEndDate(b);
      setHalfDays({});
      setSelectingAnchor(null);
      const status = getDayStatus(dateKey);
      setEditingDateKey(status.canToggleHalf ? dateKey : null);
      return;
    }
    const inRange = startDate && endDate && dateKey >= startDate && dateKey <= endDate;
    if (inRange) {
      const status = getDayStatus(dateKey);
      if (status.canToggleHalf) {
        setEditingDateKey(dateKey);
        return;
      }
    }
    setStartDate(dateKey);
    setEndDate(dateKey);
    setHalfDays({});
    setSelectingAnchor(dateKey);
    const status = getDayStatus(dateKey);
    setEditingDateKey(status.canToggleHalf ? dateKey : null);
  };

  const handleStartInput = (val) => {
    setStartDate(val);
    setSelectingAnchor(null);
    const nextEnd = endDate && val > endDate ? val : endDate;
    if (endDate && val > endDate) setEndDate(val);
    if (val) {
      const d = new Date(`${val}T12:00:00`);
      if (!Number.isNaN(d.getTime())) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
      setEditingDateKey(val);
    }
    if (editingDateKey && (editingDateKey < val || (nextEnd && editingDateKey > nextEnd))) {
      setEditingDateKey(val);
    }
  };

  const handleEndInput = (val) => {
    setEndDate(val);
    setSelectingAnchor(null);
    if (startDate && val < startDate) setStartDate(val);
    if (val) setEditingDateKey(val);
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const openModal = () => {
    if (startDate) {
      const d = new Date(`${startDate}T12:00:00`);
      if (!Number.isNaN(d.getTime())) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
      const status = getDayStatus(startDate);
      setEditingDateKey(status.canToggleHalf ? startDate : null);
    }
    setSelectingAnchor(null);
    setShowDateModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) return;
    const start = new Date(`${startDate}T12:00:00`);
    const end = new Date(`${endDate}T12:00:00`);
    if (end < start) return;
    const hasHalfDays = Object.keys(activeHalfDays).length > 0;
    onSubmit({
      startDate,
      endDate,
      halfDay: legacyHalfDay || undefined,
      halfDayOn: legacyHalfDayOn || undefined,
      halfDays: hasHalfDays ? activeHalfDays : undefined,
      comment: comment.trim(),
      leaveTypeCode: leaveTypeCode || 'annual',
      files: selectedFiles.length ? selectedFiles : undefined,
    });
    setSelectedFiles([]);
    setHalfDays({});
  };

  const calendarCells = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0);
    const startOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const monthTitle = firstDay.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(viewYear, viewMonth, d, 12, 0, 0);
      const dateKey = toDateKey(dateObj);
      const inRange = Boolean(startDate && endDate && dateKey >= startDate && dateKey <= endDate);
      const status = getDayStatus(dateKey);
      const halfPart = inRange ? activeHalfDays[dateKey] : null;
      days.push({ dayNum: d, dateKey, inRange, status, halfPart });
    }
    return { startOfWeek, days, monthTitle };
  }, [viewYear, viewMonth, startDate, endDate, activeHalfDays, holidaySet, employeeWorkingSettings, overrideWindows]);

  const activeEditingDate = useMemo(() => {
    if (!editingDateKey || !startDate || !endDate) return null;
    if (editingDateKey < startDate || editingDateKey > endDate) return null;
    const status = getDayStatus(editingDateKey);
    if (!status.canToggleHalf) return null;
    return {
      dateKey: editingDateKey,
      portion: activeHalfDays[editingDateKey] || 'full',
      amDisabled: status.contractHalf === 'afternoon',
      pmDisabled: status.contractHalf === 'morning',
    };
  }, [editingDateKey, startDate, endDate, activeHalfDays, holidaySet, employeeWorkingSettings, overrideWindows]);

  const dateSummaryText = useMemo(() => {
    if (!startDate || !endDate) return 'Select dates';
    const rangeStr = startDate === endDate
      ? formatReadableDate(startDate)
      : `${formatReadableDate(startDate)} – ${formatReadableDate(endDate)}`;
    const countStr = daysTaken != null ? `${daysTaken} day${daysTaken === 1 ? '' : 's'}` : '';
    return countStr ? `${rangeStr} · ${countStr}` : rangeStr;
  }, [startDate, endDate, daysTaken]);

  return (
    <form onSubmit={handleSubmit} className="mb-4">
      <div className="row g-3 align-items-end">
        <div className="col-12 col-md-5">
          <label className="form-label small" id="leave-type-label">Leave type</label>
          <div className="leave-type-field">
            <button
              type="button"
              className="leave-field-control"
              aria-labelledby="leave-type-label"
              aria-haspopup="listbox"
              aria-expanded={leaveTypeOpen}
              onClick={() => setLeaveTypeOpen((open) => !open)}
            >
              <span>{REQUEST_TYPES.find((t) => t.code === leaveTypeCode)?.name || 'Annual leave'}</span>
              <i className={`bi ${leaveTypeOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />
            </button>
            {leaveTypeOpen && (
              <ul className="leave-type-menu" role="listbox" aria-labelledby="leave-type-label">
                {REQUEST_TYPES.map((t) => (
                  <li key={t.code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={t.code === leaveTypeCode}
                      className="leave-type-option"
                      onClick={() => {
                        setLeaveTypeCode(t.code);
                        setLeaveTypeOpen(false);
                      }}
                    >
                      {t.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="col-12 col-md-7">
          <label className="form-label small d-block" htmlFor="dates-duration-trigger">Dates & duration</label>
          <button
            id="dates-duration-trigger"
            type="button"
            aria-label="Select dates"
            className="leave-field-control leave-dates-control"
            onClick={openModal}
          >
            <i className="bi bi-calendar3" aria-hidden="true" />
            <span>{dateSummaryText}</span>
          </button>
        </div>
      </div>

      {(() => {
        const selected = REQUEST_TYPES.find((t) => t.code === leaveTypeCode);
        return selected?.instructions ? (
          <div className="form-text text-muted mt-1 mb-0">
            <i className="bi bi-info-circle me-1" />
            {selected.instructions}
          </div>
        ) : null;
      })()}

      <div className="row g-3 align-items-end mt-1">
        <div className="col">
          <label className="form-label small">Reason (optional)</label>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="e.g. Family trip"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <div className="col-auto">
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit request'}
          </button>
        </div>
      </div>

      <div className="row g-2 mt-1">
        <div className="col-12">
          <label className="form-label small">Attach documents (optional)</label>
          <p className="small text-muted mb-1">
            PDF, DOC, DOCX or images. Max {MAX_FILE_MB}MB per file, up to {MAX_ATTACHMENTS} files. e.g. medical certificate, military duty document.
          </p>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <input
              type="file"
              className="form-control form-control-sm"
              style={{ maxWidth: 260 }}
              accept={ACCEPT_ATTACHMENTS}
              multiple
              onChange={handleFileChange}
              disabled={selectedFiles.length >= MAX_ATTACHMENTS}
            />
            {selectedFiles.map((f, i) => (
              <span key={i} className="badge bg-secondary d-inline-flex align-items-center gap-1">
                {f.name}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  style={{ fontSize: '0.6rem' }}
                  aria-label="Remove"
                  onClick={() => removeFile(i)}
                />
              </span>
            ))}
          </div>
        </div>
      </div>

      {showDateModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="leaveDatePickerModalTitle"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.45)' }}
          onClick={(e) => e.target === e.currentTarget && setShowDateModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 740 }}
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg leave-picker-modal-content">
              <div className="modal-header border-bottom px-4 py-3">
                <h6 className="modal-title fw-semibold mb-0" id="leaveDatePickerModalTitle">
                  <i className="bi bi-calendar3 me-2 text-success" />
                  Select leave dates
                </h6>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowDateModal(false)} />
              </div>

              <div className="modal-body p-0">
                <div className="row g-0">
                  <div className="col-12 col-md-5 p-4 d-flex flex-column justify-content-between leave-picker-left-pane">
                    <div>
                      <div className="mb-3">
                        <label className="form-label small fw-medium text-secondary mb-1">Start date</label>
                        <input
                          type="date"
                          aria-label="From date"
                          className="form-control leave-picker-date-input"
                          value={startDate}
                          onChange={(e) => handleStartInput(e.target.value)}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label small fw-medium text-secondary mb-1">End date</label>
                        <input
                          type="date"
                          aria-label="To date"
                          className="form-control leave-picker-date-input"
                          value={endDate}
                          onChange={(e) => handleEndInput(e.target.value)}
                        />
                      </div>

                      <div className="p-3 rounded-3 bg-white border mb-3">
                        <div className="small text-secondary mb-1">Total leave</div>
                        <div className="fs-4 fw-bold text-success">
                          {daysTaken != null ? `${daysTaken} day${daysTaken === 1 ? '' : 's'} counted` : '–'}
                        </div>
                        {Object.keys(activeHalfDays).length > 0 && (
                          <div className="mt-2 pt-2 border-top d-flex flex-column gap-1">
                            {Object.entries(activeHalfDays)
                              .sort(([a], [b]) => a.localeCompare(b))
                              .map(([k, part]) => (
                                <div key={k} className="d-flex align-items-center justify-content-between small">
                                  <span className="text-dark">
                                    {formatDayHeading(k)}: <strong>{part === 'am' ? 'Morning off' : 'Afternoon off'}</strong>
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 text-muted text-decoration-none"
                                    title="Reset to full day"
                                    onClick={() => setDatePortion(k, 'full')}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-3 small text-secondary pt-2">
                      <span><span className="days-breakdown-legend days-breakdown-counted" />Full day</span>
                      <span><span className="days-breakdown-legend days-breakdown-half" />Half day (0.5d)</span>
                      <span><span className="days-breakdown-legend days-breakdown-weekend" />Off / Holiday</span>
                    </div>
                  </div>

                  <div className="col-12 col-md-7 p-4 bg-white d-flex flex-column justify-content-between">
                    <div>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <button
                          type="button"
                          className="btn btn-sm btn-light border px-2 py-1"
                          aria-label="Previous month"
                          onClick={handlePrevMonth}
                        >
                          <i className="bi bi-chevron-left" />
                        </button>
                        <span className="fw-semibold">{calendarCells.monthTitle}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border px-2 py-1"
                          aria-label="Next month"
                          onClick={handleNextMonth}
                        >
                          <i className="bi bi-chevron-right" />
                        </button>
                      </div>

                      <div className="days-breakdown-picker-grid">
                        {WEEKDAYS.map((wd) => (
                          <div key={wd} className="days-breakdown-cell days-breakdown-header">{wd}</div>
                        ))}
                        {Array.from({ length: calendarCells.startOfWeek }, (_, i) => (
                          <div key={`empty-${i}`} className="days-breakdown-cell days-breakdown-empty" />
                        ))}
                        {calendarCells.days.map(({ dayNum, dateKey, inRange, status, halfPart }) => {
                          let cellClass = 'days-breakdown-cell ';
                          let pillTag = null;
                          if (inRange) {
                            if (status.kind === 'holiday') {
                              cellClass += 'days-breakdown-holiday';
                            } else if (status.kind === 'weekend') {
                              cellClass += 'days-breakdown-weekend';
                            } else if (status.kind === 'non_working') {
                              cellClass += 'days-breakdown-nonworking';
                            } else if (halfPart === 'am' || halfPart === 'pm') {
                              cellClass += 'days-breakdown-half';
                              pillTag = halfPart === 'am' ? 'AM off' : 'PM off';
                            } else if (status.kind === 'contract_half') {
                              cellClass += 'days-breakdown-half';
                              pillTag = '0.5d';
                            } else {
                              cellClass += 'days-breakdown-counted';
                            }
                          } else if (status.kind === 'weekend') {
                            cellClass += 'days-breakdown-out text-muted';
                          } else {
                            cellClass += 'days-breakdown-out text-dark';
                          }

                          if (activeEditingDate && activeEditingDate.dateKey === dateKey) {
                            cellClass += ' days-breakdown-cell-active';
                          }

                          const portionText = halfPart === 'am'
                            ? 'morning off'
                            : halfPart === 'pm'
                              ? 'afternoon off'
                              : 'full day';

                          return (
                            <button
                              key={dateKey}
                              type="button"
                              className={cellClass}
                              aria-label={`${dateKey}, ${inRange ? portionText : 'not selected'}`}
                              onClick={() => handleCalendarCellClick(dateKey)}
                            >
                              <span style={{ lineHeight: 1.1 }}>{dayNum}</span>
                              {pillTag && <span className="days-breakdown-pill-tag">{pillTag}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-3 pt-2">
                      {activeEditingDate ? (
                        <div className="leave-day-portion-bar d-flex flex-wrap align-items-center justify-content-between gap-2">
                          <span className="small fw-semibold text-dark">
                            {formatDayHeading(activeEditingDate.dateKey)}:
                          </span>
                          <div className="btn-group btn-group-sm" role="group" aria-label="Day duration options">
                            <button
                              type="button"
                              className={`btn ${activeEditingDate.portion === 'full' ? 'btn-success' : 'btn-outline-success bg-white'}`}
                              onClick={() => setDatePortion(activeEditingDate.dateKey, 'full')}
                            >
                              Full day
                            </button>
                            <button
                              type="button"
                              className={`btn ${activeEditingDate.portion === 'am' ? 'btn-success' : 'btn-outline-success bg-white'}`}
                              disabled={activeEditingDate.amDisabled}
                              onClick={() => setDatePortion(activeEditingDate.dateKey, 'am')}
                            >
                              Morning off
                            </button>
                            <button
                              type="button"
                              className={`btn ${activeEditingDate.portion === 'pm' ? 'btn-success' : 'btn-outline-success bg-white'}`}
                              disabled={activeEditingDate.pmDisabled}
                              onClick={() => setDatePortion(activeEditingDate.dateKey, 'pm')}
                            >
                              Afternoon off
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="small text-secondary text-center py-2">
                          Select any green working day above to make it a half day.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-light border-top px-4 py-2 d-flex justify-content-end">
                <button type="button" className="btn btn-primary btn-sm px-4" onClick={() => setShowDateModal(false)}>
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

export default HolidayRequestForm;
